import type {
  BaselineCapacityTableDataType,
  BaselineCapacityTimeslotApiResponseType,
  TableCellType,
  TableDataType,
  TimeSlotPreviewTableType,
  TimeSlotsAPIResponseType
} from '@/types/CM/updateCapacity'

import type {
  NextAvailableTimeslotApiResponseType,
  NextAvailableTimeslotTableDataType,
  MonthlyCapacityCalendarDataType,
  ViewMonthlyCapacityAPIResponseType,
  CapacityBucketsSummaryResponseType,
  BucketUtilisationRecordType,
  PresetFilterType,
  RAPBucketRow
} from '@/types/CM/viewCapacity'

import { formatIsoDate, getActivationPeriodAsNumber } from '../genericFormatting'
import { sortTimeslotsInAscendingOrder } from '../sorting'

import { DateTime } from 'luxon'

// format time ranges so its in proper 24hr format. eg. 7:00 => 07:00
function formatTimeRange (startTime: DateTime, endTime: DateTime) : string {
  const formatTime = (time: DateTime) => {
    const formattedTime = time.toFormat('HH:mm')
    return formattedTime
  }
  const startTimeFormatted = formatTime(startTime)
  const endTimeFormatted = formatTime(endTime)

  return `${startTimeFormatted} - ${endTimeFormatted}`
}

export function getDateKeysList (apiResponseData: TimeSlotsAPIResponseType | null) : string[] {
  if (apiResponseData === null) {
    return []
  }

  const dateKeysList = [] as string[]
  apiResponseData.forEach(item => {
    const dateKey = DateTime.fromISO(item.startDateTime).toFormat('yyyy-MM-dd') // "2023-12-22"
    if (!dateKeysList.includes(dateKey)) {
      dateKeysList.push(dateKey)
    }
  })
  return dateKeysList
}

export function getTimeRangeKeysList (apiResponseData: TimeSlotsAPIResponseType | null) : string[] {
  if (apiResponseData === null) {
    return []
  }

  const timeRangeKeysList = [] as string[]
  apiResponseData.forEach(item => {
    const startDateTime = DateTime.fromISO(item.startDateTime)
    const endDateTime = DateTime.fromISO(item.endDateTime)
    const timeRangeKey = formatTimeRange(startDateTime, endDateTime) // 07:00 - 8:00

    if (!timeRangeKeysList.includes(timeRangeKey)) {
      timeRangeKeysList.push(timeRangeKey)
    }
  })

  timeRangeKeysList.sort()

  return timeRangeKeysList
}

export function getCellValueFromApiResponse (
  apiResponseData: TimeSlotsAPIResponseType, dateKey: string, timeRangeKey: string
) : TableCellType {
  const cellValue = {
    baselineCapacity: 0,
    bookedCapacity: 0,
    maxTotal: 0,
    available: 0,
    status: 'INACTIVE'
  } as TableCellType

  apiResponseData.forEach(item => {
    const timeRangeId = item.id
    const startDateTime = DateTime.fromISO(item.startDateTime)
    const endDateTime = DateTime.fromISO(item.endDateTime)
    const itemDateKey = startDateTime.toFormat('yyyy-MM-dd') // "YYYY-MM-DD"
    const itemTimeRangeKey = formatTimeRange(startDateTime, endDateTime) // 07:00 - 8:00

    if (itemDateKey === dateKey && itemTimeRangeKey === timeRangeKey) {
      cellValue.timeRangeId = timeRangeId
      cellValue.baselineCapacity = item.baselineCapacity
      cellValue.bookedCapacity = item.bookedCapacity
      cellValue.maxTotal = item.maxTotalCapacity
      cellValue.available = (item.maxTotalCapacity - item.bookedCapacity)
      cellValue.status = item.status
      cellValue.appointments = item.appointments || []
    }
  })

  return cellValue
}

export function mapUpdateCapacityResponseToTableData (apiResponseData: TimeSlotsAPIResponseType): TableDataType {
  const tableData = {} as TableDataType

  const dateKeysList = getDateKeysList(apiResponseData)
  const timeRangeKeysList = getTimeRangeKeysList(apiResponseData)

  timeRangeKeysList.sort()

  timeRangeKeysList.forEach(timeRangeKey => {
    tableData[timeRangeKey] = {}
    dateKeysList.forEach(dateKey => {
      tableData[timeRangeKey][dateKey] = getCellValueFromApiResponse(apiResponseData, dateKey, timeRangeKey)
    })
  })

  return tableData
}

export function mapUpdateTimeslotAvailabilityResponseToTableData (
  apiResponseData: TimeSlotsAPIResponseType, selectedBucketIds: string[]
) : TimeSlotPreviewTableType {
  const previewTableData = {} as TimeSlotPreviewTableType

  selectedBucketIds.forEach(bucketId => {
    const filterApiResponse = apiResponseData.filter(timeslot => timeslot.bucketId === bucketId)
    const mappedData = mapUpdateCapacityResponseToTableData(filterApiResponse)
    previewTableData[bucketId] = mappedData
  })

  return previewTableData
}

export function mapNextAvailableTimeslotsResponseToTableData (
  apiResponseData: NextAvailableTimeslotApiResponseType
): NextAvailableTimeslotTableDataType {
  const tableData = [] as NextAvailableTimeslotTableDataType

  apiResponseData.forEach(item => {
    const { id, name, shortName, jobProfiles } = item

    const longName = name
    const nextAvailableTimeslot = formatDateAndTime(item.nextAvailableTimeslotStartDateTime)
    const unitsAvailable = calculateUnitsAvailable(item.nextAvailableTimeslotMaxTotalCapacity, item.nextAvailableTimeslotBookedCapacity)
    const leadTime = formatLeadTime(item.leadTime)
    const nextAvailableInHours = formatNextAvailableInHours(item.nextAvailableTimeslotStartDateTime)
    const unitsMaxTotal = item.nextAvailableTimeslotMaxTotalCapacity ? item.nextAvailableTimeslotMaxTotalCapacity : '-'

    tableData.push({
      id,
      longName,
      shortName,
      jobProfiles,
      nextAvailableTimeslot,
      unitsAvailable,
      unitsMaxTotal,
      nextAvailableInHours,
      leadTime
    })
  })

  return tableData
}

function formatDateAndTime (dateTimeString: string | undefined): string {
  if (!dateTimeString) return '-'
  return DateTime.fromISO(dateTimeString).toFormat('dd/MM/yyyy, HH:mm')
}

function calculateUnitsAvailable (
  nextAvailableTimeslotMaxTotalCapacity: number | undefined,
  nextAvailableTimeslotBookedCapacity: number | undefined
): string | number {
  if (nextAvailableTimeslotMaxTotalCapacity === undefined || nextAvailableTimeslotBookedCapacity === undefined) return '-'

  return (nextAvailableTimeslotMaxTotalCapacity - nextAvailableTimeslotBookedCapacity)
}

function formatLeadTime (leadTime: number | undefined): string {
  if (leadTime === undefined) return '-'

  const hours = Math.trunc(leadTime / 60)
  const minutes = Math.trunc(leadTime % 60).toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

function formatNextAvailableInHours (nextAvailableTimeslotStartDateTime: string | undefined): string {
  if (nextAvailableTimeslotStartDateTime === undefined) return '-'

  const now = DateTime.now()
  const nextAvailableTime = DateTime.fromISO(nextAvailableTimeslotStartDateTime)
  const diffInHours = nextAvailableTime.diff(now, 'hours')
  const diffInMinutes = nextAvailableTime.diff(now, 'minutes')
  const hours = Math.trunc(diffInHours.hours)

  const minutes = Math.abs(Math.trunc(diffInMinutes.minutes % 60)).toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

export function mapViewMonthlyCapacityResponseToCalendarData (
  apiResponseData: ViewMonthlyCapacityAPIResponseType
): MonthlyCapacityCalendarDataType {
  const tableData = {} as MonthlyCapacityCalendarDataType

  apiResponseData.forEach(item => {
    const date = new Date(item.date)
    const dateKey = formatIsoDate(date) // "YYYY-MM-DD"

    tableData[dateKey] = {
      bookedCapacity: item.bookedCapacity,
      totalCapacity: item.totalCapacity,
      state: item.state
    }
  })

  return tableData
}

export function mapUpdateTimeslotAvailabilityResponseToRecordType (
  apiResponseData: CapacityBucketsSummaryResponseType
) : BucketUtilisationRecordType[] {
  const previewTableData = [] as BucketUtilisationRecordType[]

  apiResponseData.forEach((record) => {
    const activationPeriodString = record.rollingConfiguration.rollingTimeslotActivationPeriod.toString()
    const activationPeriod = parseInt(activationPeriodString.replace(/P|D/g, ''))

    previewTableData.push({
      id: record.id,
      name: record.name,
      shortName: record.shortName,
      workType: record.workType,
      rollingTimeslotActivationPeriod: activationPeriod,
      bookedCapacity: 0,
      totalCapacity: 0,
      totalUtilisation: 0,
      dailyUtilisation: 0,
      nextActiveTimeslotStartDateTime: ''
    })
  })

  return previewTableData
}

export function sortBaselineCapacityTimeslotsByWeekday (data: BaselineCapacityTimeslotApiResponseType): BaselineCapacityTimeslotApiResponseType {
  // ApiResponse is not sorted by default
  // Order of weekdays for sorting
  const weekdays = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ]

  const sortedData = {} as BaselineCapacityTimeslotApiResponseType

  // Sort the data by weekday
  weekdays.forEach((weekday) => {
    // Ensure the weekday exists in the data
    if (data[weekday.toLowerCase()]) {
      const timeslots = data[weekday.toLowerCase()]

      sortedData[weekday] = sortTimeslotsInAscendingOrder(timeslots)
    }
  })

  return sortedData
}

export function mapBaselineCapacityResponseToTableData (
  apiResponseData: BaselineCapacityTimeslotApiResponseType
) : BaselineCapacityTableDataType {
  const tableData = {} as BaselineCapacityTableDataType

  Object.entries(apiResponseData).forEach(([weekDayKey, timeSlots]) => {
    timeSlots.forEach(timeslot => {
      const startTime = DateTime.fromISO(timeslot.startTime).toFormat('HH:mm')
      const endTime = DateTime.fromISO(timeslot.endTime).toFormat('HH:mm')

      const timeRangeKey = `${startTime} - ${endTime}`

      // Create the time range entry if it doesn't exist
      tableData[timeRangeKey] = tableData[timeRangeKey] || {}

      tableData[timeRangeKey][weekDayKey] = {
        baselineCapacity: Number(timeslot.baselineCapacity) // Sometimes coming as string from api
      }
    })
  })

  return tableData
}

export function getPresetFiltersForWorkSupplier (presetFilters: PresetFilterType[], workSupplier: string): PresetFilterType[] {
  return presetFilters.filter((filter) => {
    return filter.organisation.name.toLowerCase() === workSupplier.toLowerCase()
  })
}

export function mapRAPResponseToTableRow (apiResponseData: CapacityBucketsSummaryResponseType): RAPBucketRow[] {
  return apiResponseData.map((bucket) => {
    return {
      id: bucket.id,
      workType: bucket.workType,
      shortName: bucket.shortName,
      longName: bucket.name,
      rollingTimeslotActivationPeriod: getActivationPeriodAsNumber(bucket.rollingConfiguration.rollingTimeslotActivationPeriod)
    }
  })
}
